import { Component, DestroyRef, ElementRef, HostListener, computed, effect, inject, signal, untracked, viewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { SessionService } from '../core/tenant/session.service';
import { FeatureService } from '../core/tenant/feature.service';
import { TableService } from '../services/table.service';
import { destinoAlEntrar } from '../features/ruta/entrada-a-ruta';
import { AYUDA_DE_PANTALLAS, AyudaDePantalla, BIENVENIDA, BOTONES } from './ayuda';
import { ayudaDeLaUrl, claveDeAyuda, claveDeEstado, guardarEstado, leerEstado, pendientes, recorridoPendiente, sitioDeLaBurbuja } from './bienvenida';

interface Tarjeta { titulo: string; paraQue: string; comoSeUsa: string[]; }

/** Rutas donde no va nada: sin sesión, o el Modo Ruta, que pinta su propio marco en un teléfono. */
const SIN_AYUDA = ['/login', '/register', '/reset'];
/** Ayudas 26/10 (B.6): el Modo Ruta y el Modo Bodega tienen su (?), pero no la bienvenida ni el recorrido de la caja (Z50, Z93). */
const MODOS = ['/ruta', '/bodega'];

/**
 * Bienvenida de la primera vez y el botón (?) de la pantalla abierta (plan ONBOARDING-Y-AYUDA, M16).
 *
 * - `<dialog>` nativo y no Swal: Swal no apila, y el recorrido no debe tapar ni ser tapado por una alerta.
 * - Sin zone.js: todo el estado es de signals, así que se repinta tras el `await` de la sesión sin marcar nada.
 * - La venta rápida escucha `document:keydown` (letras a la búsqueda, doble Esc vacía la orden): el diálogo corta la
 *   propagación de sus teclas, para que cerrar el recorrido con Esc no vacíe una orden.
 */
@Component({
  selector: 'app-ayuda',
  standalone: true,
  template: `
    @if (visible()) {
      <!-- 7.6 y Z127 (guía 26/10): en los modos, abajo a la derecha (arriba tapaba «Cerrar sesión» y «sin enviar»), encima de las barras fijas. -->
      <button type="button" class="ayuda-boton" [class.abajo]="enUnModo()" [style.bottom.px]="enUnModo() ? sobreLasBarras() : null"
              (click)="abrirDeLaPantalla()" [attr.aria-label]="botones.ayuda" [title]="botones.ayuda">?</button>
    }
    <dialog #dialogo class="ayuda-dialogo" (close)="alCerrar()" (keydown)="alPulsar($event)" aria-labelledby="ayuda-titulo">
      @if (tarjeta(); as t) {
        <h2 id="ayuda-titulo">{{ t.titulo }}</h2>
        @if (t.paraQue) { <p>{{ t.paraQue }}</p> }
        @if (t.comoSeUsa.length) {
          <h3>{{ botones.comoSeUsa }}</h3>
          <ol>
            @for (p of t.comoSeUsa; track $index) { <li>{{ p }}</li> }
          </ol>
        }
        <div class="ayuda-acciones">
          @if (enRecorrido()) {
            <button type="button" class="ayuda-omitir" (click)="omitir()">{{ botones.omitir }}</button>
            <span class="ayuda-paso">{{ paso() + 1 }} / {{ tarjetas().length }}</span>
            @if (paso() > 0) { <button type="button" (click)="paso.set(paso() - 1)">{{ botones.anterior }}</button> }
            @if (paso() < tarjetas().length - 1) {
              <button type="button" class="ayuda-siguiente primario" (click)="paso.set(paso() + 1)">{{ botones.siguiente }}</button>
            } @else {
              <button type="button" class="ayuda-listo primario" (click)="cerrar()">{{ botones.listo }}</button>
            }
          } @else {
            <button type="button" class="ayuda-cerrar primario" (click)="cerrar()">{{ botones.cerrar }}</button>
          }
        </div>
      }
    </dialog>
    @if (pasoActual(); as p) {
      @if (sitio()?.aro; as a) {
        <div class="ayuda-aro" aria-hidden="true" [style.top.px]="a.top" [style.left.px]="a.left" [style.width.px]="a.width" [style.height.px]="a.height"></div>
      }
      <div #burbuja class="ayuda-burbuja" role="dialog" aria-labelledby="ayuda-burbuja-texto" tabindex="-1" (keydown)="alPulsarEnLaBurbuja($event)"
           [style.top.px]="sitio()?.burbuja?.top" [style.left.px]="sitio()?.burbuja?.left">
        <p id="ayuda-burbuja-texto">{{ p.texto }}</p>
        <div class="ayuda-acciones">
          <button type="button" class="ayuda-omitir" (click)="terminar()">{{ botones.omitir }}</button>
          <span class="ayuda-paso">{{ indice() + 1 }} / {{ pasos().length }}</span>
          @if (indice() > 0) { <button type="button" class="ayuda-anterior" (click)="anteriorPaso()">{{ botones.anterior }}</button> }
          @if (indice() < pasos().length - 1) {
            <button type="button" class="ayuda-siguiente primario" (click)="siguientePaso()">{{ botones.siguiente }}</button>
          } @else {
            <button type="button" class="ayuda-listo primario" (click)="terminar()">{{ botones.listo }}</button>
          }
        </div>
      </div>
    }`,
  styles: [`
    .ayuda-boton { position: fixed; top: 12px; right: 16px; z-index: 60; width: 36px; height: 36px; border-radius: 50%;
      border: 1px solid var(--color-slate-200, #e2e8f0); background: var(--color-card-light); color: var(--color-slate-600, #475569);
      font-weight: 700; cursor: pointer; }
    .ayuda-boton.abajo { top: auto; bottom: calc(16px + env(safe-area-inset-bottom)); box-shadow: 0 2px 8px rgba(15, 23, 42, 0.2); }
    .ayuda-dialogo { max-width: min(520px, calc(100vw - 32px)); border: none; border-radius: 12px; padding: 24px;
      background: var(--color-card-light); color: var(--color-slate-800, #1e293b); }
    .ayuda-dialogo::backdrop { background: rgba(15, 23, 42, 0.5); }
    .ayuda-dialogo h2 { margin: 0 0 8px; font-size: 1.25rem; }
    .ayuda-dialogo h3 { margin: 16px 0 4px; font-size: 1rem; }
    .ayuda-dialogo ol { margin: 0; padding-left: 20px; }
    .ayuda-acciones { display: flex; flex-wrap: wrap; align-items: center; justify-content: flex-end; gap: 8px; margin-top: 24px; }
    .ayuda-acciones button { padding: 8px 14px; border-radius: 8px; border: 1px solid var(--color-slate-300, #cbd5e1); background: transparent; color: inherit; cursor: pointer; }
    .ayuda-acciones button.primario { background: var(--color-primary, #0f172a); color: #fff; border-color: transparent; }
    .ayuda-omitir { margin-right: auto; }
    .ayuda-paso { color: var(--color-slate-600, #475569); font-size: 0.875rem; }
    /* A7: el aro deja la pantalla usable debajo (pointer-events: none) y oscurece el resto con una sombra enorme. */
    .ayuda-aro { position: fixed; z-index: 1000; pointer-events: none; border-radius: 8px; outline: 2px solid var(--color-accent, #a88a6d);
      box-shadow: 0 0 0 9999px rgba(15, 23, 42, 0.5); transition: top 150ms ease, left 150ms ease, width 150ms ease, height 150ms ease; }
    .ayuda-burbuja { position: fixed; z-index: 1001; width: min(320px, calc(100vw - 24px)); box-sizing: border-box; padding: 16px; border-radius: 12px;
      background: var(--color-card-light); color: var(--color-slate-800, #1e293b); box-shadow: 0 10px 30px rgba(15, 23, 42, 0.25); outline: none; }
    .ayuda-burbuja p { margin: 0; font-size: 0.95rem; line-height: 1.45; }
    .ayuda-burbuja .ayuda-acciones { margin-top: 14px; }
    @media (prefers-reduced-motion: reduce) { .ayuda-aro { transition: none; } }
  `],
})
export class AyudaComponent {
  private readonly session = inject(SessionService);
  private readonly features = inject(FeatureService);
  private readonly mesas = inject(TableService);
  private readonly router = inject(Router);
  readonly botones = BOTONES;

  private readonly dialogo = viewChild.required<ElementRef<HTMLDialogElement>>('dialogo');

  /** Las ayudas que se consultan; un campo para que la prueba ponga las suyas. */
  ayudas: readonly AyudaDePantalla[] = AYUDA_DE_PANTALLAS;

  private readonly url = signal(this.router.url);
  // Z50 y Z93: el recorrido y el (?) son de la caja. Por a dónde entra la sesión y no solo por URL: al entrar, la URL aún es
  // `/` cuando la sesión aparece, y el recorrido se abría encima del Modo Bodega (bodeguero) o del Modo Ruta (vendedor con
  // `ruta`). La misma regla que decide a dónde entra cada uno (destinoAlEntrar).
  readonly enUnModo = computed(() => MODOS.some(r => this.url().startsWith(r)));
  /**
   * En los modos, la distancia al fondo que deja libres las barras de abajo (`data-barra-abajo`: las pestañas de Ruta y el pie
   * de enviar del pedido, pegado encima). null = ninguna acaba en la mitad de abajo de la pantalla: vale el `bottom` del CSS.
   */
  readonly sobreLasBarras = signal<number | null>(null);
  /** El botón (?): en la caja y, desde Ayudas 26/10, también dentro del Modo Ruta y del Modo Bodega. */
  readonly visible = computed(() => { const s = this.session.session();
    return !!s && !SIN_AYUDA.some(r => this.url().startsWith(r)) && (this.enUnModo() || destinoAlEntrar(s) === '/pos'); });
  /** La bienvenida y los recorridos: solo para quien entra a la caja y está en ella. */
  private readonly deLaCaja = computed(() => { const s = this.session.session();
    return !!s && destinoAlEntrar(s) === '/pos' && !SIN_AYUDA.some(r => this.url().startsWith(r)) && !this.enUnModo(); });

  readonly tarjetas = signal<Tarjeta[]>([]);
  readonly paso = signal(0);
  readonly enRecorrido = signal(false);
  readonly tarjeta = computed(() => this.tarjetas()[this.paso()] ?? null);

  private mostradas: AyudaDePantalla[] = [];
  private clave: string | null = null;

  // ---- A7: el recorrido de la pantalla (los pasos que señalan dónde dar clic). Todo en signals: sin zone.js. ----
  private readonly burbuja = viewChild<ElementRef<HTMLElement>>('burbuja');
  readonly pasos = signal<{ ancla: string; texto: string }[]>([]);
  readonly indice = signal(0);
  readonly sitio = signal<{ aro: { top: number; left: number; width: number; height: number } | null; burbuja: { top: number; left: number } } | null>(null);
  private readonly enRecorridoDePantalla = signal(false);
  readonly pasoActual = computed(() => (this.enRecorridoDePantalla() ? this.pasos()[this.indice()] ?? null : null));
  private modulo: AyudaDePantalla | null = null;
  private elemento: HTMLElement | null = null;
  /** Cuánto se espera a que la pantalla pinte sus anclas. La prueba lo pone en 0. */
  espera = 400;

  medirBarras(): void {
    if (!this.enUnModo()) { this.sobreLasBarras.set(null); return; }
    const alto = window.innerHeight;
    const topes = Array.from(document.querySelectorAll<HTMLElement>('[data-barra-abajo]'))
      .map(b => b.getBoundingClientRect())
      // DrDev002: por dónde ACABA en la mitad de abajo, no por dónde empieza: con el teclado abierto o en horizontal el pie
      // de enviar es alto para la pantalla y empieza en la mitad de arriba.
      .filter(r => r.height > 0 && r.bottom > alto / 2 && r.top < alto)
      .map(r => r.top);
    this.sobreLasBarras.set(topes.length ? Math.ceil(alto - Math.min(...topes)) + 12 : null);
  }

  constructor() {
    // Las barras cambian con la ruta, el tamaño de la ventana, el desplazamiento (el pie es sticky) y el contenido.
    // Como mucho una medición pendiente: el desplazamiento dispara muchos eventos por cuadro.
    let pendiente = false;
    const medir = () => {
      if (pendiente) return;
      pendiente = true;
      requestAnimationFrame(() => { pendiente = false; this.medirBarras(); });
    };
    window.addEventListener('resize', medir, { passive: true });
    document.addEventListener('scroll', medir, { passive: true, capture: true });
    const observador = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(medir);
    observador?.observe(document.body);
    inject(DestroyRef).onDestroy(() => {
      window.removeEventListener('resize', medir);
      document.removeEventListener('scroll', medir, { capture: true });
      observador?.disconnect();
    });
    effect(() => { this.url(); untracked(medir); });
    this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(e => { this.url.set(e.urlAfterRedirects); this.quizaRecorridoDePantalla(); });
    effect(() => {
      const s = this.session.session();
      if (!s || !this.deLaCaja()) return;
      this.session.modules(); // un módulo nuevo en la sesión vuelve a mirar
      const flujo = this.mesas.flujoDeVenta();
      untracked(() => this.quizaAbrirRecorrido(claveDeEstado(s.tenantId, s.userId ?? s.userName), flujo));
    });
  }

  private quizaAbrirRecorrido(clave: string, flujo: ReturnType<TableService['flujoDeVenta']>): void {
    if (this.clave === clave || this.dialogo().nativeElement.open) return;
    this.clave = clave;
    const estado = leerEstado(clave);
    const faltan = pendientes(this.ayudas, m => this.features.has(m), flujo, estado);
    if (!faltan.length) { this.quizaRecorridoDePantalla(); return; }
    this.mostradas = faltan;
    const intro: Tarjeta[] = estado.vistos.length ? [] : [{ titulo: BIENVENIDA.titulo, paraQue: BIENVENIDA.intro, comoSeUsa: [] }];
    this.abrir([...intro, ...faltan], true);
  }

  abrirDeLaPantalla(): void {
    const a = ayudaDeLaUrl(this.ayudas, this.router.url, this.mesas.flujoDeVenta());
    // A7: con pasos, el (?) repite el recorrido de la pantalla; sin ellos, la tarjeta de siempre.
    if (a?.pasos?.length) { this.iniciarRecorrido(a); return; }
    this.mostradas = [];
    this.abrir([a ?? { titulo: BOTONES.sinAyudaTitulo, paraQue: '', comoSeUsa: [] }], false);
  }

  private abrir(tarjetas: Tarjeta[], recorrido: boolean): void {
    this.tarjetas.set(tarjetas);
    this.paso.set(0);
    this.enRecorrido.set(recorrido);
    const d = this.dialogo().nativeElement;
    if (d.open) return;
    if (typeof d.showModal === 'function') d.showModal(); else d.setAttribute('open', '');
  }

  omitir(): void {
    if (this.clave) guardarEstado(this.clave, { ...leerEstado(this.clave), omitido: true });
    this.mostradas = [];
    this.cerrar();
  }

  /**
   * Las teclas del diálogo no llegan a la venta rápida. Esc cierra el diálogo de forma nativa, pero Chrome retrasa el
   * evento `close` en una pestaña en segundo plano (medido el 04/10): lo visto se guarda aquí, al pulsar.
   */
  alPulsar(e: KeyboardEvent): void {
    e.stopPropagation();
    if (e.key === 'Escape') this.alCerrar();

  }

  /** El evento `close` llega en otra tarea: lo visto se guarda aquí, sin esperarlo. */
  cerrar(): void {
    this.alCerrar();
    const d = this.dialogo().nativeElement;
    if (typeof d.close === 'function') d.close(); else d.removeAttribute('open');
  }

  /** Listo, Cerrar o Esc (evento `close`): lo enseñado queda visto. Idempotente. */
  alCerrar(): void {
    if (this.clave && this.mostradas.length) {
      const e = leerEstado(this.clave);
      guardarEstado(this.clave, { ...e, vistos: [...new Set([...e.vistos, ...this.mostradas.map(claveDeAyuda)])] });
    }
    this.mostradas = [];
    this.enRecorrido.set(false);
    // Tras la bienvenida, la pantalla en la que se está puede tener su recorrido.
    this.quizaRecorridoDePantalla();
  }

  // ---- A7 ----------------------------------------------------------------------------------------------------------
  private quizaRecorridoDePantalla(): void {
    if (!this.clave || this.enRecorridoDePantalla() || this.dialogo().nativeElement.open) return;
    const a = recorridoPendiente(ayudaDeLaUrl(this.ayudas, this.router.url, this.mesas.flujoDeVenta()), leerEstado(this.clave));
    if (!a) return;
    setTimeout(() => { if (!this.enRecorridoDePantalla() && !this.dialogo().nativeElement.open) this.iniciarRecorrido(a); }, this.espera);
  }

  private iniciarRecorrido(a: AyudaDePantalla): void {
    this.modulo = a;
    this.pasos.set(a.pasos ?? []);
    this.enRecorridoDePantalla.set(true);
    this.mostrar(0, 1);
  }

  /** Pinta el paso `i` o, si su ancla no está (sin favoritos, otro modo de la caja), el siguiente en esa dirección. */
  private mostrar(i: number, direccion: 1 | -1): void {
    const pasos = this.pasos();
    for (let j = i; j >= 0 && j < pasos.length; j += direccion) {
      // Comillas y barras escapadas a mano: CSS.escape no existe en todos los motores (jsdom), y el ancla es una clave fija.
      const el = document.querySelector(`[data-ayuda="${pasos[j].ancla.replace(/["\\]/g, '\\$&')}"]`) as HTMLElement | null;
      // Lo que la caja no muestra sale del DOM con @if; lo que queda oculto por estilo tampoco cuenta.
      if (!el || el.closest('[hidden]') || getComputedStyle(el).display === 'none') continue;
      this.indice.set(j);
      this.elemento = el;
      el.scrollIntoView?.({ block: 'center', inline: 'nearest' });
      this.colocar();
      setTimeout(() => { this.colocar(); this.burbuja()?.nativeElement.focus(); });
      return;
    }
    if (direccion === 1) this.terminar(); else this.mostrar(i + 1, 1);
  }

  @HostListener('window:resize')
  colocar(): void {
    if (!this.enRecorridoDePantalla()) return;
    const b = this.burbuja()?.nativeElement;
    const r = this.elemento?.getBoundingClientRect() ?? null;
    this.sitio.set({
      aro: r ? { top: r.top - 4, left: r.left - 4, width: r.width + 8, height: r.height + 8 } : null,
      burbuja: sitioDeLaBurbuja(r, { ancho: b?.offsetWidth || 320, alto: b?.offsetHeight || 140 }, { ancho: window.innerWidth, alto: window.innerHeight }),
    });
  }

  siguientePaso(): void { this.mostrar(this.indice() + 1, 1); }
  anteriorPaso(): void { this.mostrar(this.indice() - 1, -1); }

  /**
   * Las teclas de la burbuja no llegan a la venta rápida (letras a la búsqueda, ⏎ cobra, doble Esc vacía la orden):
   * cerrar el recorrido con Esc no puede vaciar una orden.
   */
  alPulsarEnLaBurbuja(e: KeyboardEvent): void {
    e.stopPropagation();
    if (e.key === 'Escape') this.terminar();
  }

  /** Listo, Omitir o Esc: el recorrido de esta pantalla queda hecho (el (?) lo repite). */
  terminar(): void {
    if (this.clave && this.modulo) {
      const e = leerEstado(this.clave);
      guardarEstado(this.clave, { ...e, recorridos: [...new Set([...(e.recorridos ?? []), claveDeAyuda(this.modulo)])] });
    }
    this.modulo = null;
    this.elemento = null;
    this.enRecorridoDePantalla.set(false);
    this.pasos.set([]);
    this.sitio.set(null);
  }
}
